import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Flame, Star, Award, Sparkles } from 'lucide-react';
import { useGamification } from '../context/GamificationContext';
import { BadgeTier } from '../types';
import { isGamificationMoment } from '../lib/gamificationMoments';

const TIER_GRADIENT: Record<BadgeTier, string> = {
  bronze: 'from-orange-400 to-amber-600',
  silver: 'from-gray-300 to-gray-500',
  gold: 'from-yellow-300 to-amber-500',
  platinum: 'from-indigo-400 to-purple-600',
};

/**
 * Full-screen celebratory moment for the most significant gamification events.
 * Renders the first "moment" notification (see isGamificationMoment); lesser
 * notifications are handled by GamificationToast. Mounted once, app-wide.
 */
export const GamificationMoment: React.FC = () => {
  const { notifications, dismissNotification } = useGamification();
  const moment = notifications.find(isGamificationMoment);
  const firedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!moment || firedFor.current === moment.id) return;
    firedFor.current = moment.id;
    // Two quick bursts from the lower corners for a celebratory feel.
    const opts = { particleCount: 80, spread: 70, disableForReducedMotion: true } as const;
    confetti({ ...opts, origin: { x: 0.2, y: 0.7 }, angle: 60 });
    confetti({ ...opts, origin: { x: 0.8, y: 0.7 }, angle: 120 });
  }, [moment]);

  const close = () => {
    if (moment) dismissNotification(moment.id);
  };

  const renderIcon = () => {
    if (!moment) return null;
    if (moment.type === 'level_up') {
      return (
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-berry to-primary-600 flex items-center justify-center shadow-lg shadow-primary-600/30 mx-auto">
          <span className="text-white font-bold text-3xl leading-none">{moment.level ?? ''}</span>
        </div>
      );
    }
    if (moment.type === 'streak_milestone') {
      return (
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/30 mx-auto">
          <Flame className="h-10 w-10 text-white" />
        </div>
      );
    }
    // badge_earned (gold/platinum)
    const tier = moment.badge?.tier ?? 'gold';
    return (
      <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${TIER_GRADIENT[tier]} flex items-center justify-center shadow-lg mx-auto ring-4 ring-white`}>
        <Award className="h-10 w-10 text-white drop-shadow" />
      </div>
    );
  };

  const eyebrow =
    moment?.type === 'level_up'
      ? 'Level up!'
      : moment?.type === 'streak_milestone'
        ? 'Streak milestone'
        : `New badge${moment?.badge?.tier ? ` · ${moment.badge.tier}` : ''}`;

  return (
    <AnimatePresence>
      {moment && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
        >
          <motion.div
            className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center"
            initial={{ opacity: 0, scale: 0.85, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 12 }}
            transition={{ type: 'spring', stiffness: 320, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            {renderIcon()}
            <div className="mt-5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-primary-700">
              <Sparkles className="h-3.5 w-3.5" /> {eyebrow}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mt-1">{moment.title}</h2>
            <p className="text-gray-600 mt-2">{moment.message}</p>
            <button
              type="button"
              onClick={close}
              className="mt-6 w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 rounded-lg shadow-lg shadow-primary-600/30 transition-colors"
            >
              {moment.type === 'level_up' ? 'Keep going' : 'Nice!'}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
